import { defineComponent } from 'vue';
import {
    QCard,
    QCardSection,
    QCardActions,
    QBtn,
    QInput,
    QIcon,
    QSeparator,
} from 'quasar';
import utils from '../../../Shared/utils.jsx';
import {
    isValidJsIdentifier,
    suggestScopeIncludeAlias,
} from './controlDesignerScopeScript.js';

function dedupeUserControlsByName(items) {
    const seen = new Set();
    const result = [];

    for (const item of items || []) {
        const name = String(item?.Name || '').trim();
        if (!name || seen.has(name))
            continue;
        seen.add(name);
        result.push(item);
    }

    return result;
}

function browseRowKey(item, index) {
    const name = String(item?.Name || '').trim();
    const id = item?.Id ?? item?.id ?? item?.$objectId ?? index;
    return `${name}::${id}`;
}

function cloneIncludes(value) {
    return (value || []).map((item) => ({
        Path: item?.Path || '',
        As: item?.As || '',
    }));
}

function includesEqual(a, b) {
    return JSON.stringify(cloneIncludes(a)) === JSON.stringify(cloneIncludes(b));
}

function validateIncludes(includes) {
    const used = new Set();
    const errors = [];

    for (const item of includes) {
        const path = String(item?.Path || '').trim();
        const alias = String(item?.As || '').trim();

        if (!path)
            errors.push('Each include must have a Path.');

        if (!alias || !isValidJsIdentifier(alias))
            errors.push(`Invalid alias "${alias || ''}" for "${path || 'include'}".`);

        if (alias && used.has(alias))
            errors.push(`Duplicate alias "${alias}".`);
        else if (alias)
            used.add(alias);
    }

    return errors;
}

const compactCellStyle = {
    padding: '1px 4px',
    verticalAlign: 'middle',
    lineHeight: '18px',
    fontSize: '11px',
};

export default defineComponent({
    name: 'ControlDesignerScopeIncludesEditor',
    components: {
        QCard,
        QCardSection,
        QCardActions,
        QBtn,
        QInput,
        QIcon,
        QSeparator,
    },
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['update:modelValue', 'close', 'validation-change'],
    data() {
        return {
            list: [],
            filter: '',
            draftIncludes: cloneIncludes(this.modelValue),
            validationErrors: [],
        };
    },
    watch: {
        modelValue: {
            deep: true,
            handler(value) {
                const next = cloneIncludes(value);
                if (!includesEqual(next, this.draftIncludes))
                    this.draftIncludes = next;
            },
        },
    },
    mounted() {
        this.refresh();
        this.revalidate();
    },
    computed: {
        browseList() {
            return dedupeUserControlsByName(this.list);
        },
        filteredList() {
            const filter = String(this.filter || '').trim().toLowerCase();
            return this.browseList.filter((item) => {
                const name = String(item?.Name || '');
                return !filter || name.toLowerCase().includes(filter);
            });
        },
        selectedPaths() {
            return new Set(this.draftIncludes.map((item) => item.Path));
        },
        hasValidationErrors() {
            return this.validationErrors.length > 0;
        },
    },
    methods: {
        async refresh() {
            this.list = await utils.api.get('Apps/UserControls/List');
        },
        revalidate() {
            this.validationErrors = validateIncludes(this.draftIncludes);
            this.$emit('validation-change', this.validationErrors);
        },
        emitDraft() {
            this.revalidate();
            if (this.hasValidationErrors)
                return false;

            this.$emit('update:modelValue', cloneIncludes(this.draftIncludes));
            return true;
        },
        addInclude(path) {
            const normalizedPath = String(path || '').trim();
            if (!normalizedPath || this.selectedPaths.has(normalizedPath))
                return;

            const usedAliases = this.draftIncludes.map((item) => item.As);
            this.draftIncludes = [
                ...this.draftIncludes,
                {
                    Path: normalizedPath,
                    As: suggestScopeIncludeAlias(normalizedPath, usedAliases),
                },
            ];
            this.revalidate();
        },
        removeInclude(path) {
            this.draftIncludes = this.draftIncludes.filter((item) => item.Path !== path);
            this.revalidate();
        },
        updateAlias(path, alias) {
            this.draftIncludes = this.draftIncludes.map((item) => (
                item.Path === path ? { ...item, As: alias } : item
            ));
            this.revalidate();
        },
        aliasHasError(alias) {
            if (!alias || !isValidJsIdentifier(alias))
                return true;

            let count = 0;
            for (const item of this.draftIncludes) {
                if (item.As === alias)
                    count += 1;
            }

            return count > 1;
        },
        onCloseClick() {
            if (!this.emitDraft())
                return;

            this.$emit('close');
        },
        onCancelClick() {
            this.cancelDraft();
            this.$emit('close');
        },
        cancelDraft() {
            this.draftIncludes = cloneIncludes(this.modelValue);
            this.validationErrors = [];
            this.$emit('validation-change', this.validationErrors);
        },
        openIncludeDocument(path, event) {
            event?.stopPropagation?.();
            event?.preventDefault?.();
            const docName = String(path || '').trim();
            if (!docName)
                return;
            utils.openForLocalEdit({ Name: docName, Type: 'UserControl' });
        },
    },
    render() {
        const browseRows = this.filteredList.length > 0
            ? this.filteredList.map((item, index) => {
                const name = item?.Name || '';
                const selected = this.selectedPaths.has(name);

                return (
                    <tr key={browseRowKey(item, index)}>
                        <td style={{ ...compactCellStyle, width: '22px' }}>
                            <QBtn
                                flat
                                dense
                                size="xs"
                                padding="none"
                                icon={selected ? 'mdi-check' : 'mdi-plus'}
                                color={selected ? 'grey-6' : 'primary'}
                                disable={selected}
                                onClick={() => this.addInclude(name)}
                            />
                        </td>
                        <td style={compactCellStyle}>
                            <span
                                style={{
                                    cursor: selected ? 'default' : 'pointer',
                                    color: selected ? 'inherit' : '#1565c0',
                                    textDecoration: selected ? 'none' : 'underline',
                                }}
                                onClick={() => !selected && this.addInclude(name)}
                            >
                                {name}
                            </span>
                        </td>
                    </tr>
                );
            })
            : [<tr key="loading"><td colSpan={2} style={compactCellStyle}>Loading...</td></tr>];

        const selectedRows = this.draftIncludes.length > 0
            ? this.draftIncludes.map((item, index) => (
                <tr key={`${item.Path}::${index}`}>
                    <td style={compactCellStyle}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', maxWidth: '100%' }}>
                            <span style={{ wordBreak: 'break-all' }}>{item.Path}</span>
                            <QBtn
                                flat
                                dense
                                size="xs"
                                padding="none"
                                icon="mdi-open-in-new"
                                color="primary"
                                title="Open in document editor"
                                onClick={(event) => this.openIncludeDocument(item.Path, event)}
                            />
                        </span>
                    </td>
                    <td style={{ ...compactCellStyle, width: '140px' }}>
                        <div class="property-grid">
                            <QInput
                                modelValue={item.As}
                                dense
                                outlined
                                hide-bottom-space
                                error={this.aliasHasError(item.As)}
                                onUpdate:modelValue={(value) => this.updateAlias(item.Path, value)}
                            />
                        </div>
                    </td>
                    <td style={{ ...compactCellStyle, width: '22px' }}>
                        <QBtn
                            flat
                            dense
                            size="xs"
                            padding="none"
                            icon="mdi-close"
                            color="negative"
                            onClick={() => this.removeInclude(item.Path)}
                        />
                    </td>
                </tr>
            ))
            : [<tr key="empty"><td colSpan={3} style={compactCellStyle}>No includes selected.</td></tr>];

        const sectionHeadingStyle = {
            fontWeight: 600,
            fontSize: '11px',
            marginBottom: '2px',
            lineHeight: 1.2,
        };

        return (
            <QCard
                class="control-designer-scope-includes-editor"
                style={{ display: 'flex', flexDirection: 'column', maxHeight: '80vh', width: '100%' }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '26px',
                        padding: '0 8px',
                        backgroundColor: 'var(--q-primary)',
                        color: '#fff',
                        gap: '4px',
                    }}
                >
                    <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, lineHeight: 1 }}>Scope Includes</span>
                    <QBtn flat dense round size="xs" padding="none sm" icon="mdi-refresh" color="white" onClick={() => this.refresh()} />
                </div>

                <QCardSection style={{ padding: '4px 8px' }}>
                    <div class="property-grid">
                        <QInput
                            placeholder="Search"
                            outlined
                            dense
                            hide-bottom-space
                            clearable
                            autocomplete="off"
                            modelValue={this.filter}
                            onUpdate:modelValue={(value) => { this.filter = value; }}
                            vSlots={{ prepend: () => <QIcon name="mdi-magnify" size="14px" /> }}
                        />
                    </div>
                </QCardSection>

                <QCardSection style={{ minHeight: '140px', maxHeight: '160px', overflowY: 'auto', padding: '0 8px 4px' }}>
                    <div style={sectionHeadingStyle}>Browse</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>{browseRows}</tbody>
                    </table>
                </QCardSection>

                <QSeparator />

                <QCardSection style={{ minHeight: '120px', maxHeight: '160px', overflowY: 'auto', padding: '4px 8px' }}>
                    <div style={sectionHeadingStyle}>Selected imports</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th align="left" style={{ ...compactCellStyle, fontWeight: 600 }}>Path</th>
                                <th align="left" style={{ ...compactCellStyle, fontWeight: 600 }}>As</th>
                                <th style={compactCellStyle}></th>
                            </tr>
                        </thead>
                        <tbody>{selectedRows}</tbody>
                    </table>
                    {this.hasValidationErrors ? (
                        <div style={{ color: '#b00020', marginTop: '4px', fontSize: '11px', lineHeight: 1.3 }}>
                            {this.validationErrors.map((message, index) => (
                                <div key={`${index}-${message}`}>{message}</div>
                            ))}
                        </div>
                    ) : null}
                </QCardSection>

                <QCardActions align="right" style={{ padding: '2px 8px', minHeight: '28px' }}>
                    <QBtn flat dense size="sm" padding="none sm" color="grey-8" onClick={() => this.onCancelClick()}>Cancel</QBtn>
                    <QBtn flat dense size="sm" padding="none sm" color="green-8" disable={this.hasValidationErrors} onClick={() => this.onCloseClick()}>Close</QBtn>
                </QCardActions>
            </QCard>
        );
    },
});
